<script>
import { apiVarietyList } from '@/api/analysis'

export default {
  name: "VarietySelector",
  props: {
    showCombineBtn: {
      type: Boolean,
      default: () => false
    },
    varieties: {
      type: Array,
      default: () => [],
      required: true
    }
  },
  data() {
    return {
      groupNames: {
        agricultural: "农副组",
        financial: "金融组",
        energy: "能化组",
        black: "黑色系",
        metal: "金属组",
        shipping: "航线",
        option: "期权组",
        other: "其他"
      },
      isCheckedAll: false,
      isCombined: false,
      isNetProfit: false,
      checkedGroups: [],
      checkedItems: [],
      isCheckAllIndeterminate: false,
      groupIndeterminateMap: {},
      showCustomVarietyGroupDialog: false,
      customGroups: [], // 自定义分组列表
      selectedCustomGroup: null, // 弹窗中选中的自定义分组
      editingGroup: null, // 正在编辑的分组
      editingGroupName: '', // 编辑中的分组名称
      dialogCheckedVarieties: [], // 弹窗中选中的品种
      allVarieties: [] // 所有品种数据
    }
  },
  methods: {
    handleCheckAllChange(val) {
      this.checkedGroups = val ? this.groups.map(item => {
        return item.code
      }) : [];
      this.checkedItems = val ? this.varieties.map(item => {
        return item.code
      }) : [];
    },
    handleGroupItemChange(val, event) {
      const checkedGroupItems = this.groupVarieties[event.target.value];
      this.checkedItems = val
          ? [...new Set([...this.checkedItems, ...checkedGroupItems])]
          : this.checkedItems.filter(item => !checkedGroupItems.includes(item));
    },
    handleCombineChange(val) {
      this.$emit('combineChange', val);
    },
    handleNetProfitChange(val) {
      this.$emit('typeChange', val);
    },
    // 打开自定义分组弹窗
    openCustomVarietyGroupBtnClick() {
      this.showCustomVarietyGroupDialog = true;
      this.resetDialogState();
    },
    // 重置弹窗状态
    resetDialogState() {
      this.selectedCustomGroup = null;
      this.editingGroup = null;
      this.editingGroupName = '';
      this.dialogCheckedVarieties = [];
    },
    // 选择自定义分组
    selectCustomGroup(group) {
      this.selectedCustomGroup = group;
      this.dialogCheckedVarieties = [...(group.items || [])];
      this.editingGroup = group;
      this.editingGroupName = group.name;
    },
    // 开始添加新分组
    startAddGroup() {
      this.resetDialogState();
    },
    // 验证分组名称
    validateGroupName(groupName, currentGroup = null) {
      if (!groupName || !groupName.trim()) {
        this.$message.warning('请输入分组名称');
        return false;
      }

      const allGroupNames = [...Object.values(this.groupNames), ...this.customGroups.map(g => g.name)];
      const duplicateName = allGroupNames.find(name => name === groupName.trim());
      
      if (duplicateName && (!currentGroup || currentGroup.name !== groupName.trim())) {
        this.$message.warning('分组名称已存在，请使用其他名称');
        return false;
      }

      return true;
    },
    // 保存分组（添加或修改）
    saveGroup() {
      const groupName = this.editingGroupName?.trim();
      
      if (!this.validateGroupName(groupName, this.editingGroup)) {
        return;
      }

      if (!this.dialogCheckedVarieties || this.dialogCheckedVarieties.length === 0) {
        this.$message.warning('请至少选择一个品种');
        return;
      }

      if (this.editingGroup) {
        // 修改分组
        this.editingGroup.name = groupName;
        this.editingGroup.items = [...this.dialogCheckedVarieties];
        this.selectedCustomGroup = this.editingGroup;
        this.$message.success('修改分组成功');
      } else {
        // 添加新分组
        const newGroup = {
          code: `custom_${Date.now()}`,
          name: groupName,
          items: [...this.dialogCheckedVarieties]
        };
        this.customGroups.push(newGroup);
        this.selectedCustomGroup = newGroup;
        this.editingGroup = newGroup;
        this.$message.success('添加分组成功');
      }

      this.saveCustomGroupsToStorage();
    },
    // 删除分组
    deleteGroupFromDialog(group) {
      this.$confirm(`确定要删除分组"${group.name}"吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.removeGroup(group);
        this.$message.success('删除成功');
      }).catch(() => {});
    },
    // 移除分组（内部方法）
    removeGroup(group) {
      this.customGroups = this.customGroups.filter(g => g.code !== group.code);

      // 如果删除的是当前选中的分组，清空相关状态
      if (this.selectedCustomGroup?.code === group.code) {
        this.resetDialogState();
      } else if (this.editingGroup?.code === group.code) {
        this.editingGroup = null;
        this.editingGroupName = '';
      }

      // 从主界面移除
      this.checkedGroups = this.checkedGroups.filter(code => code !== group.code);
      const groupItems = this.groupVarieties[group.code] || [];
      this.checkedItems = this.checkedItems.filter(item => !groupItems.includes(item));

      this.saveCustomGroupsToStorage();
    },
    // 关闭自定义分组弹窗
    closeCustomGroupDialog() {
      this.showCustomVarietyGroupDialog = false;
      this.resetDialogState();
    },
    // 获取所有品种数据
    async fetchAllVarieties() {
      try {
        const response = await apiVarietyList();
        if (response.data.code === 0 && response.data.data) {
          this.allVarieties = response.data.data;
        }
      } catch (e) {
        this.allVarieties = [];
      }
    },
    // 从 localStorage 加载自定义分组
    loadCustomGroupsFromStorage() {
      try {
        const stored = localStorage.getItem('variety_custom_groups');
        if (stored) {
          this.customGroups = JSON.parse(stored);
        } else {
          this.customGroups = [];
        }
      } catch (e) {
        this.customGroups = [];
      }
    },
    // 清理无效的自定义分组
    cleanInvalidCustomGroups(validVarietyCodes) {
      const beforeLength = this.customGroups.length;
      
      this.customGroups.forEach(customGroup => {
        customGroup.items = customGroup.items.filter(item => validVarietyCodes.includes(item));
      });
      
      this.customGroups = this.customGroups.filter(customGroup => customGroup.items.length > 0);

      if (beforeLength !== this.customGroups.length) {
        this.saveCustomGroupsToStorage();
      }
    },
    // 保存自定义分组到 localStorage
    saveCustomGroupsToStorage() {
      try {
        localStorage.setItem('variety_custom_groups', JSON.stringify(this.customGroups));
      } catch (e) {
        // 保存失败时静默处理
      }
    }
  },
  computed: {
    groups() {
      // 默认分组
      const defaultGroups = [...new Set(this.varieties.map(item => item.group))].map(groupCode => ({
        code: groupCode,
        name: this.groupNames[groupCode],
        isCustom: false
      }));

      // 将期权组放到默认分组的最后面
      const optionIndex = defaultGroups.findIndex(g => g.code === 'option');
      if (optionIndex > -1) {
        const optionGroup = defaultGroups.splice(optionIndex, 1)[0];
        defaultGroups.push(optionGroup);
      }

      // 自定义分组（显示在默认分组后面）
      const customGroups = this.customGroups.map(customGroup => ({
        code: customGroup.code,
        name: customGroup.name,
        isCustom: true
      }));

      return [...defaultGroups, ...customGroups];
    },
    groupVarieties() {
      // 获取所有有效的品种代码（用于验证自定义分组）
      const allValidVarietyCodes = new Set(this.allVarieties.map(item => item.code));

      // 默认分组的品种
      const defaultGroupVarieties = this.varieties.reduce((result, item) => {
        if (!result[item.group]) {
          result[item.group] = [];
        }
        result[item.group].push(item.code);
        return result;
      }, {});

      // 自定义分组的品种（只包含有效的品种，基于 allVarieties）
      const customGroupVarieties = this.customGroups.reduce((result, customGroup) => {
        result[customGroup.code] = (customGroup.items || []).filter(code => allValidVarietyCodes.has(code));
        return result;
      }, {});

      return { ...defaultGroupVarieties, ...customGroupVarieties };
    },
    // 常规分组（除期权以外的）
    normalVarieties() {
      return this.varieties.filter(item => item.group !== 'option');
    },
    // 期权分组
    optionVarieties() {
      return this.varieties.filter(item => item.group === 'option');
    }
  },
  mounted() {
    // 组件挂载时加载自定义分组
    this.loadCustomGroupsFromStorage();
    // 获取所有品种数据
    this.fetchAllVarieties();
  },
  watch: {
    checkedItems: {
      handler() {
        // 判断全选按钮是否勾选
        this.isCheckedAll = this.checkedItems.length === this.varieties.length;

        // 判断分组按钮是否勾选
        this.groups.forEach(item => {
          const isAllChecked = this.groupVarieties[item.code].every(element => this.checkedItems.includes(element));
          this.checkedGroups = isAllChecked
              ? [...new Set([...this.checkedGroups, item.code])]
              : this.checkedGroups.filter(element => element !== item.code);
        });

        // 判断全选按钮是否显示部分选择状态
        this.isCheckAllIndeterminate = this.checkedItems.length > 0 && this.checkedItems.length < this.varieties.length;

        // 判断分组按钮是否显示部分选择状态
        this.groups.forEach(item => {
          let everyChecked = this.groupVarieties[item.code].every(element => this.checkedItems.includes(element))
          let someChecked = this.groupVarieties[item.code].some(element => this.checkedItems.includes(element))
          this.groupIndeterminateMap[item.code] = !everyChecked && someChecked
        });

        this.$emit('change', this.checkedItems);
      },
    },
    varieties: {
      handler() {
        const varietyCodes = this.varieties.map(item => item.code);
        
        // 清理选中的品种
        this.checkedItems = this.checkedItems.filter(item => varietyCodes.includes(item));
        if (this.checkedItems.length === 0 && varietyCodes.length > 0) {
          this.checkedItems = [varietyCodes[0]];
        }

        // 清理自定义分组中无效的品种
        this.cleanInvalidCustomGroups(varietyCodes);
      },
      deep: true,
      immediate: true,
    }
  }
}
</script>

<template>
  <div class="variety-selector">
    <div class="left-btn-box">
      <el-checkbox label="全选" :indeterminate="isCheckAllIndeterminate" v-model="isCheckedAll" border
                   @change="handleCheckAllChange"></el-checkbox>
      <el-checkbox label="组合" v-model="isCombined" v-if="showCombineBtn" border
                   @change="handleCombineChange"></el-checkbox>
      <el-checkbox label="净利润" v-model="isNetProfit" border @change="handleNetProfitChange"></el-checkbox>
    </div>
    <div class="selecter-group-box">
      <div class="group-list-wrapper">
        <el-checkbox-group v-model="checkedGroups" size="small">
          <span v-for="item in groups" :key="item.code" class="group-item-wrapper">
            <el-checkbox :label="item.code"
                         :indeterminate="groupIndeterminateMap[item.code]" @change="handleGroupItemChange">
              {{ item.name }}
            </el-checkbox>
          </span>
          <span class="group-item-wrapper">
            <label class="custom-group-btn" @click="openCustomVarietyGroupBtnClick">
              <i class="el-icon-setting"></i> 自定义分组
            </label>
          </span>
        </el-checkbox-group>
      </div>
    </div>
    <div class="selecter-item-box">
      <el-checkbox-group v-model="checkedItems" size="small">
        <div class="normal-varieties" v-if="normalVarieties.length > 0">
          <el-checkbox v-for="item in normalVarieties" :label="item.code" :key="item.code">{{ item.name }}</el-checkbox>
        </div>
        <div class="option-divider" v-if="normalVarieties.length > 0 && optionVarieties.length > 0"></div>
        <div class="option-varieties" v-if="optionVarieties.length > 0">
          <el-checkbox v-for="item in optionVarieties" :label="item.code" :key="item.code">{{ item.name }}</el-checkbox>
        </div>
      </el-checkbox-group>
    </div>
    <el-dialog
        title="自定义分组"
        :visible.sync="showCustomVarietyGroupDialog"
        width="800px"
        @close="closeCustomGroupDialog">
      <div class="custom-group-dialog-content">
        <!-- 左侧：分组名称列表 -->
        <div class="group-name-list">
          <div class="list-header">
            <el-button type="primary" size="small" @click="startAddGroup" icon="el-icon-plus">添加分组</el-button>
          </div>
          <div class="list-body">
            <div 
              v-for="group in customGroups" 
              :key="group.code"
              class="group-item"
              :class="{ active: selectedCustomGroup && selectedCustomGroup.code === group.code }"
              @click="selectCustomGroup(group)">
              <span class="group-name">{{ group.name }}</span>
              <div class="group-actions">
                <i class="el-icon-delete" @click.stop="deleteGroupFromDialog(group)" title="删除"></i>
              </div>
            </div>
            <div v-if="customGroups.length === 0" class="empty-tip">暂无自定义分组</div>
          </div>
        </div>
        <!-- 右侧：品种列表 -->
        <div class="group-variety-items">
          <div class="variety-header">
            <div class="edit-form">
              <el-input 
                v-model="editingGroupName" 
                placeholder="请输入分组名称"
                size="small"></el-input>
            </div>
          </div>
          <div class="variety-body">
            <div class="variety-list">
              <el-checkbox-group v-model="dialogCheckedVarieties" size="small" class="variety-checkbox-group">
                <el-checkbox 
                  v-for="item in allVarieties" 
                  :label="item.code" 
                  :key="item.code"
                  class="variety-checkbox-item">
                  {{ item.name }}
                </el-checkbox>
              </el-checkbox-group>
              <div v-if="allVarieties.length === 0" class="empty-tip">暂无品种数据</div>
            </div>
            <div class="save-actions">
              <el-button type="primary" size="small" @click="saveGroup">保存</el-button>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
// 颜色变量
$primary-color: #409EFF;
$danger-color: #F56C6C;
$text-color: #303133;
$text-secondary: #909399;
$border-color: #DCDFE6;
$bg-light: #F5F7FA;
$bg-active: #ECF5FF;

.variety-selector {
  position: relative;
  padding-left: 100px;
  width: 100%;
  min-height: 200px;
  box-sizing: border-box;

  .el-checkbox {
    ::v-deep(.el-checkbox__input.is-focus) {
      .el-checkbox__inner {
        border-color: $border-color;
      }
    }

    ::v-deep(.el-checkbox__input.is-checked) {
      .el-checkbox__inner {
        border-color: $primary-color;
      }
    }
  }

  .left-btn-box {
    padding: 10px 10px 10px 0;
    position: absolute;
    left: 0;
    top: 0;
    width: 100px;
    bottom: 0;
    text-align: left;
    box-sizing: border-box;

    .el-checkbox {
      width: 100%;
      margin-right: 0;
      padding: 6px 2px 6px 10px;
      height: auto;
    }

    .el-checkbox + .el-checkbox {
      margin-left: 0;
      margin-top: 10px;
    }

    .el-button {
      margin-left: 0;
      margin-top: 10px;
      height: 33px;
      width: 90px;
      line-height: 33px;
      padding: 0;
      font-weight: normal;
    }
  }

  .selecter-group-box {
    padding: 10px;
    text-align: left;
    border-bottom: 1px solid $border-color;

    .group-list-wrapper {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
    }

    .group-item-wrapper {
      display: inline-flex;
      align-items: center;
      margin-right: 10px;
      margin-bottom: 5px;
    }

    .custom-group-btn {
      font-size: 14px;
      cursor: pointer;

      &:hover {
        color: $primary-color;
      }
    }
  }

  .selecter-item-box {
    padding: 10px;
    text-align: left;

    .el-checkbox {
      width: 115px;
      overflow-x: hidden;
      margin-right: 10px;
      margin-bottom: 5px;
    }

    .option-divider {
      border-top: 1px dashed $border-color;
      margin: 10px 0;
    }
  }

  // 自定义分组弹窗样式
  .custom-group-dialog-content {
    display: flex;
    height: 500px;
    border: 1px solid $border-color;
    border-radius: 4px;
    overflow: hidden;

    .group-name-list {
      width: 250px;
      border-right: 1px solid $border-color;
      display: flex;
      flex-direction: column;
      background-color: $bg-light;

      .list-header {
        padding: 15px;
        border-bottom: 1px solid $border-color;
        background-color: #fff;
      }

      .list-body {
        flex: 1;
        overflow-y: auto;
        padding: 10px;

        .group-item {
          padding: 10px 15px;
          margin-bottom: 5px;
          background-color: #fff;
          border: 1px solid $border-color;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: all 0.2s;

          &:hover {
        border-color: $primary-color;
        background-color: $bg-active;

            .group-actions {
              opacity: 1;
            }
          }

          &.active {
        border-color: $primary-color;
        background-color: $bg-active;
            color: $primary-color;
          }

          .group-name {
            flex: 1;
            font-size: 14px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .group-actions {
            display: flex;
            gap: 8px;
            opacity: 0;
            transition: opacity 0.2s;

            i {
              font-size: 16px;
              color: $text-secondary;
              cursor: pointer;

              &:hover {
                color: $primary-color;
              }

              &.el-icon-delete:hover {
                color: $danger-color;
              }
            }
          }
        }

        .empty-tip {
          text-align: center;
          color: $text-secondary;
          padding: 20px;
          font-size: 14px;
        }
      }
    }

    .group-variety-items {
      flex: 1;
      display: flex;
      flex-direction: column;
      background-color: #fff;

      .variety-header {
        padding: 15px;
        border-bottom: 1px solid $border-color;
        background-color: $bg-light;
      }

      .variety-body {
        flex: 1;
        padding: 15px;
        display: flex;
        flex-direction: column;
        overflow: hidden;

        .variety-list {
          flex: 1;
          overflow-y: auto;
          margin-bottom: 15px;

          .variety-checkbox-group {
            display: grid;
            grid-template-columns: repeat(auto-fill, 100px);
            gap: 10px 15px;
            width: 100%;
            justify-items: start;

            .variety-checkbox-item {
              width: 100px;
              margin: 0;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              text-align: left;
              display: flex;
              align-items: center;

              ::v-deep(.el-checkbox) {
                display: flex;
                align-items: center;
              }

              ::v-deep(.el-checkbox__input) {
                display: flex;
                align-items: center;
              }

              ::v-deep(.el-checkbox__label) {
                max-width: calc(100px - 20px);
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                display: inline-block;
                text-align: left;
                line-height: 1;
                vertical-align: middle;
              }
            }
          }

          .empty-tip {
            text-align: center;
            color: $text-secondary;
            padding: 40px;
            font-size: 14px;
          }
        }

        .save-actions {
          padding-top: 15px;
          border-top: 1px solid $border-color;
          text-align: right;
          flex-shrink: 0;
        }
      }
    }
  }
}
</style>
